/** @format */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useResponsive } from "./use-responsive";
import { usePathname } from "../../utility/hooksUtil";
import {
	Avatar,
	Box,
	Drawer,
	ListItemButton,
	Stack,
	Typography,
	alpha,
} from "@mui/material";
// import Logo from "../logo/Logo";
import UseNavConfig from "./ConfigNav";
import SignOut from "../signOut/SignOut";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../redux/selectors/userSelector";
import UpdateAccountModal from "../modal/update-account-modal/UpdateAccountModal";
import { useNavigate } from "react-router-dom";
import {
	CancelMembershipSubscription,
	AccountVerificationModal,
	CreateCircleBtn,
	SubscriptionModal,
	UpdatePaymentMethodModal,
	ReviewModal,
} from "../allComponents/AllComponents";
import HowItWorksModal from "../how-it-works/HowItWorksModal";
import { RoleNames } from "../../utility/types/rolesType";
// import { selectUserSubscription } from "../../redux/selectors/subscriptionSelector";
// import { SubscriptionStatus } from "../../utility/types/subscriptionType";

export const NAV = {
	WIDTH: 280,
};

export const HEADER = {
	H_MOBILE: 64,
	H_DESKTOP: 80,
	H_DESKTOP_OFFSET: 80 - 16,
};

export interface NavProps {
	openNav: boolean;
	onCloseNav: () => void;
}

export default function DrawerNavbar(props: NavProps) {
	const pathname = usePathname();

	const userData = useSelector(selectCurrentUser);

	// const userSubscription = useSelector(selectUserSubscription);

	const navConfig = UseNavConfig();

	const { openNav, onCloseNav } = props;

	const upLg = useResponsive("up", "lg");

	const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

	const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false);

	const [openUpdatePaymentMethodModal, setOpenUpdatePaymentMethodModal] =
		useState(false);

	const [openCancelMembershipModal, setOpenCancelMembershipModal] =
		useState(false);

	const [openVerifyAccountModal, setOpenVerifyAccountModal] = useState(false);

	const [openReview, setOpenReview] = useState(false);

	const [openHowItWorks, setOpenHowItWorks] = useState(false);

	useEffect(() => {
		if (openNav) {
			onCloseNav();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname]);

	const checkRole = useCallback(
		() => (role: any) => {
			return [
				RoleNames.ADMIN,
				RoleNames.SUPER_ADMIN,
				RoleNames.UNION_LEADER,
			].includes(role.roleName || role);
		},
		[],
	);

	const handleOpenUpdateModal = useCallback(() => {
		setIsUpdateModalOpen(true);
	}, []);

	const handleCloseUpdateModal = () => {
		setIsUpdateModalOpen(false);
	};

	const handleOpenSubscriptionModal = useCallback(() => {
		setOpenSubscriptionModal(true);
	}, []);

	const handleCloseSubscriptionModal = () => {
		setOpenSubscriptionModal(false);
	};

	const handleOpenUpdatePaymentMethodModal = useCallback(() => {
		setOpenUpdatePaymentMethodModal(true);
	}, []);

	const handleCloseUpdatePaymentMethodModal = () => {
		setOpenUpdatePaymentMethodModal(false);
	};

	const handleOpenCancelMembershipModal = useCallback(() => {
		setOpenCancelMembershipModal(true);
	}, []);

	const handleCloseCancelMembershipModal = () => {
		setOpenCancelMembershipModal(false);
	};

	const handleOpenVerifyAccountModal = useCallback(() => {
		setOpenVerifyAccountModal(true);
	}, []);

	const handleCloseVerifyAccountModal = () => {
		setOpenVerifyAccountModal(false);
	};

	const handleOpenReviewModal = useCallback(() => {
		setOpenReview(true);
	}, []);

	const handleCloseReview = () => {
		setOpenReview(false);
	};

	const handleOpenHowItWorks = useCallback(() => {
		setOpenHowItWorks(true);
	}, []);

	const handleCloseHowItWorks = () => {
		setOpenHowItWorks(false);
	};

	type ModalHandlerKey =
		| "Update Account"
		| "Activate Membership"
		| "Update Payment Method"
		| "Cancel Membership"
		| "Verify Your Account"
		| "Leave A Review"
		| "How It Works";

	const renderAccount = (
		<Box
			sx={{
				mx: 2.5,
				py: 1,
				px: 2.5,
				display: "flex",
				borderRadius: 1.5,
				alignItems: "center",
				bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12),
			}}>
			<Avatar src={userData?.profileImage ?? ""} alt="photoURL" />

			<Box sx={{ ml: 2, flexShrink: 1, minWidth: 0 }}>
				<Typography
					variant="subtitle2"
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}>
					{`${userData?.firstName} ${userData?.lastName}`}
				</Typography>

				<Typography
					variant="body2"
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}>
					{userData?.email}
				</Typography>
			</Box>
		</Box>
	);

	const modalHandlers: Record<ModalHandlerKey, () => void> = useMemo(
		() => ({
			"Update Account": handleOpenUpdateModal,
			"Activate Membership": handleOpenSubscriptionModal,
			"Update Payment Method": handleOpenUpdatePaymentMethodModal,
			"Cancel Membership": handleOpenCancelMembershipModal,
			"Verify Your Account": handleOpenVerifyAccountModal,
			"Leave A Review": handleOpenReviewModal,
			"How It Works": handleOpenHowItWorks,
		}),
		[
			handleOpenUpdateModal,
			handleOpenSubscriptionModal,
			handleOpenUpdatePaymentMethodModal,
			handleOpenCancelMembershipModal,
			handleOpenVerifyAccountModal,
			handleOpenReviewModal,
			handleOpenHowItWorks,
		],
	);

	const renderCreateCircleBtn = () => {
		const shouldRenderCreateCircleBtn =
			userData?.isVerified === true && userData?.roles?.some(checkRole);

		return shouldRenderCreateCircleBtn ? <CreateCircleBtn /> : null;
	};

	/**
	 * Pinned above the scrollable nav so the primary action is never more than
	 * one glance away — it used to sit at the bottom of a long scrolling list.
	 */
	const renderPrimaryAction = () => {
		const action = renderCreateCircleBtn();
		if (!action) return null;

		const leadsCircle = (userData?.unionLeaderOf?.length ?? 0) > 0;

		return (
			<Box
				sx={{
					mx: 2,
					p: 1.75,
					borderRadius: 2,
					border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
					background: (theme) =>
						`linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
					// CreateCircleBtn is reused as-is; stretch it to the drawer width.
					"& .MuiStack-root > span": { display: "block", width: "100%" },
					"& .MuiButton-root": { width: "100%", mx: 0 },
				}}>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						fontWeight: 700,
						letterSpacing: "0.06em",
						textTransform: "uppercase",
						color: "text.secondary",
						mb: 1,
					}}>
					{leadsCircle ? "Your circles" : "Run your own circle"}
				</Typography>

				{action}
			</Box>
		);
	};

	const groupedNav = useMemo(() => {
		const order = ["Circles", "Account", "Admin"];
		const buckets = new Map<string, any[]>();

		(navConfig ?? []).forEach((item: any) => {
			const key = item?.group ?? "Account";
			if (!buckets.has(key)) buckets.set(key, []);
			buckets.get(key)!.push(item);
		});

		const known = order
			.filter((group) => buckets.has(group))
			.map((group) => ({ group, items: buckets.get(group)! }));

		const rest = Array.from(buckets.keys())
			.filter((group) => !order.includes(group))
			.map((group) => ({ group, items: buckets.get(group)! }));

		return [...known, ...rest];
	}, [navConfig]);

	const renderMenu = useMemo(
		() => (
			<Stack component="nav" spacing={1.5} sx={{ px: 2, pb: 3 }}>
				{groupedNav.map(({ group, items }) => (
					<Box key={group}>
						<Typography
							variant="caption"
							sx={{
								display: "block",
								px: 1.5,
								mb: 0.5,
								fontWeight: 700,
								fontSize: 11,
								letterSpacing: "0.08em",
								textTransform: "uppercase",
								color: "text.disabled",
							}}>
							{group}
						</Typography>
						<Stack spacing={0.25}>
							{items.map((item: any) => (
								<NavItem
									key={item?.title}
									item={item}
									modalHandlers={modalHandlers}
									onNavigate={onCloseNav}
								/>
							))}
						</Stack>
					</Box>
				))}
				<SignOut />
			</Stack>
		),
		[groupedNav, modalHandlers, onCloseNav],
	);

	const renderContent = (
		<Box
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
			}}>
			{/* <Logo sx={{ ml: 4, mb: 0.5 }} /> */}

			<Box sx={{ mt: 1.5, mb: 1.5, flexShrink: 0 }}>{renderAccount}</Box>

			<Box sx={{ mb: 2, flexShrink: 0 }}>{renderPrimaryAction()}</Box>

			<Box
				sx={{
					flexGrow: 1,
					minHeight: 0,
					overflowY: "auto",
					overflowX: "hidden",
					"&::-webkit-scrollbar": {
						width: "8px",
					},
					"&::-webkit-scrollbar-track": {
						background: "transparent",
					},
					"&::-webkit-scrollbar-thumb": {
						backgroundColor: "rgba(0,0,0,0.2)",
						borderRadius: "4px",
					},
					"&::-webkit-scrollbar-thumb:hover": {
						backgroundColor: "rgba(0,0,0,0.3)",
					},
				}}>
				{renderMenu}
			</Box>
		</Box>
	);

	return (
		<Box
			sx={{
				flexShrink: { lg: 0 },
				width: { lg: NAV.WIDTH },
			}}>
			{upLg ?
				<Box
					sx={{
						height: "100vh",
						position: "fixed",
						width: NAV.WIDTH,
						borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
						overflow: "hidden",
					}}>
					{renderContent}
				</Box>
				: <Drawer
					open={openNav}
					onClose={onCloseNav}
					PaperProps={{
						sx: {
							width: NAV.WIDTH,
							height: "100vh",
						},
					}}>
					{renderContent}
				</Drawer>
			}

			<UpdateAccountModal
				open={isUpdateModalOpen}
				handleClose={handleCloseUpdateModal}
				userData={userData}
			/>

			<SubscriptionModal
				open={openSubscriptionModal}
				handleClose={() => handleCloseSubscriptionModal()}
			/>

			<UpdatePaymentMethodModal
				open={openUpdatePaymentMethodModal}
				handleClose={handleCloseUpdatePaymentMethodModal}
			/>

			<CancelMembershipSubscription
				open={openCancelMembershipModal}
				handleClose={handleCloseCancelMembershipModal}
			/>

			<AccountVerificationModal
				open={openVerifyAccountModal}
				handleClose={handleCloseVerifyAccountModal}
			/>

			<ReviewModal open={openReview} handleClose={handleCloseReview} />

			<HowItWorksModal open={openHowItWorks} onOpenChange={setOpenHowItWorks} />
		</Box>
	);
}

DrawerNavbar.propTypes = {
	openNav: PropTypes.bool,
	onCloseNav: PropTypes.func,
};

const NavItem = React.memo(({ item, modalHandlers, onNavigate }: any) => {
	const navigate = useNavigate();
	const pathname = usePathname();

	// Modal items share a path with a real route (e.g. "How It Works" vs
	// "Dashboard"), so they must not count as the active route.
	const active = item?.path === pathname && !modalHandlers?.[item?.title];

	const handleClick = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();

		e.stopPropagation();

		// check if the item title exist in modalHandlers to determine action.
		const handler = modalHandlers[item?.title];

		if (handler) {
			handler();
		} else {
			navigate(item.path);
			setTimeout(() => {
				if (onNavigate) {
					onNavigate();
				}
			}, 100);
		}
	};

	return (
		<ListItemButton
			onClick={handleClick}
			sx={{
				minHeight: 40,
				borderRadius: 1.25,
				typography: "body2",
				color: "text.secondary",
				textTransform: "capitalize",
				fontWeight: "fontWeightMedium",
				py: 0.9,
				px: 1.5,
				position: "relative",
				transition: "background-color 0.18s ease, color 0.18s ease",
				"&:hover": {
					bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
					color: "text.primary",
				},
				...(active && {
					color: "primary.main",
					fontWeight: "fontWeightSemiBold",
					bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
					"&::before": {
						content: '""',
						position: "absolute",
						left: 0,
						top: "50%",
						transform: "translateY(-50%)",
						width: 3,
						height: 18,
						borderRadius: "0 3px 3px 0",
						bgcolor: "primary.main",
					},
					"&:hover": {
						bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
					},
				}),
				fontSize: 14,
			}}>
			<Box
				component="span"
				sx={{
					width: 20,
					height: 20,
					mr: 1.5,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
				}}>
				{item?.icon}
			</Box>

			<Box component="span">{item?.title}</Box>
		</ListItemButton>
	);
});

NavItem.prototype = {
	item: PropTypes.object,
};
